import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { createQflyTheme } from '@/theme';
import { ThemeModeContext, type ThemePref } from './themeModeContext';

// Same key the inline boot script in index.html reads. Keep them in step.
const STORAGE_KEY = 'qfly.theme';
const DARK_QUERY = '(prefers-color-scheme: dark)';

const isThemePref = (v: string | null): v is ThemePref =>
  v === 'system' || v === 'light' || v === 'dark';

/**
 * Read the persisted preference. Two separate fallback paths on purpose:
 * storage being inaccessible (private browsing, disabled storage) and
 * storage containing garbage (a stray/corrupted value, a browser extension
 * write, manual tampering) are different failure modes, and both fall back
 * to 'system' — matches `useSidebarCollapsed.ts`'s convention of keeping
 * "can't read" and "read something invalid" as distinct checks.
 */
const readPersistedPref = (): ThemePref => {
  let raw: string | null;
  try {
    raw = window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return 'system';
  }
  return isThemePref(raw) ? raw : 'system';
};

// Guards `window.matchMedia` the same way MUI's own `useMediaQuery` does —
// jsdom (and some older/embedded webviews) don't implement it, so a bare
// call would throw rather than gracefully defaulting.
const matchMediaSupported = (): boolean =>
  typeof window !== 'undefined' && typeof window.matchMedia === 'function';

const systemPrefersDark = (): boolean =>
  matchMediaSupported() ? window.matchMedia(DARK_QUERY).matches : false;

const resolveMode = (pref: ThemePref): 'light' | 'dark' => {
  if (pref === 'system') {
    return systemPrefersDark() ? 'dark' : 'light';
  }
  return pref;
};

const ORDER: readonly ThemePref[] = ['system', 'light', 'dark'];

export interface AppThemeProviderProps {
  readonly children: ReactNode;
}

/**
 * Single fused provider: owns the `pref`/`resolved` state, persists it, and
 * renders the MUI `ThemeProvider`/`CssBaseline` itself — deliberately not
 * split into a separate "theme selection" component and a separate MUI
 * wrapper, so there is exactly one place that turns `pref` into a rendered
 * theme.
 */
export const AppThemeProvider = ({ children }: AppThemeProviderProps): JSX.Element => {
  const [pref, setPref] = useState<ThemePref>(readPersistedPref);
  const [resolved, setResolved] = useState<'light' | 'dark'>(() => resolveMode(pref));

  // Apply + persist whenever the preference itself changes.
  useEffect(() => {
    setResolved(resolveMode(pref));
    try {
      window.localStorage.setItem(STORAGE_KEY, pref);
    } catch {
      // Storage is a nicety here — the theme still applies for this session.
    }
  }, [pref]);

  // Only while following the OS: track it changing under us. Gated on
  // `pref === 'system'` and keyed on `[pref]` so this cleanly
  // subscribes/unsubscribes on every system <-> explicit transition.
  useEffect(() => {
    if (pref !== 'system' || !matchMediaSupported()) return;
    const mq = window.matchMedia(DARK_QUERY);
    const onChange = (): void => setResolved(systemPrefersDark() ? 'dark' : 'light');
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [pref]);

  // Functional updater — never closes over `pref` from render scope, so a
  // rapid double-click (or a stale render) can't skip a step. Matches
  // `useSidebarCollapsed.ts`'s "don't trust a stale closure" discipline.
  const cycle = useCallback((): void => {
    setPref((current) => ORDER[(ORDER.indexOf(current) + 1) % ORDER.length]);
  }, []);

  const contextValue = useMemo(
    () => ({ pref, resolved, setPref, cycle }),
    [pref, resolved, cycle],
  );

  const theme = useMemo(() => createQflyTheme(resolved), [resolved]);

  return (
    <ThemeModeContext.Provider value={contextValue}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeModeContext.Provider>
  );
};
